import React from 'react';
import moment from 'moment';
import semver from 'semver';
import { Box, Button } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { normalizeDeviceVersion } from '../../utils/deviceVersion';
import TitanDataGrid from '../TitanDataGrid/TitanDataGrid';
import PrinterService from '../../services/PrinterService';
import { SORT_ORDERS, COMPONENT_STATUSES } from '../../constants';
import useBuildModuleState from './use-build-module-state';
import ComponentStatus from '../Component/ComponentStatus';
import StateCommentDialog from '../Fibrify/FibrifyStateCommentDialog';

export default function BuildModuleStates({ buildModule }) {
  const { buildModuleState } = useBuildModuleState(buildModule);
  const [loading, setLoading] = React.useState(true);

  const [showMaintenanceButton, setShowMaintenanceButton] = React.useState(
    false
  );

  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(10);

  const [rows, setRows] = React.useState([]);

  const [cursor, setCursor] = React.useState(null);

  const [editableState, setEditableState] = React.useState(null);

  const mapPageToCursor = React.useRef({});

  const loadStates = React.useCallback(async () => {
    const { states, lastKey } = await PrinterService.getPrinterStates(
      buildModule.id,
      {
        sort: SORT_ORDERS.DESCENDING,
        startAt: mapPageToCursor.current[page - 1],
        pageSize
      }
    );

    setRows(states);
    lastKey ? setCursor(lastKey.timestamp) : setCursor(null);
  }, [page, pageSize, setRows, setCursor]);

  const columns = React.useMemo(
    () => [
      {
        headerName: 'State',
        field: 'state',
        visibilityBreakpoint: 'sm',
        minWidth: 250,
        sortable: false,
        renderCell: ({ row }) => (
          <ComponentStatus component={{ status: row.state }} />
        )
      },
      {
        headerName: 'Time',
        field: 'time',
        visibilityBreakpoint: 'sm',
        minWidth: 180,
        sortable: false,
        renderCell: ({ row }) =>
          row.timestamp
            ? moment(row.timestamp).format('YYYY-MM-DD HH:mm:ss')
            : ''
      },
      {
        headerName: 'Comment',
        field: 'comment',
        visibilityBreakpoint: 'lg',
        flex: 1,
        minWidth: 180,
        sortable: false
      },
      {
        headerName: '',
        width: 60,
        field: 'actions',
        type: 'actions',
        getActions: params =>
          [
            {
              label:
                params.row.comment?.length > 0 ? 'Edit comment' : 'Add comment',
              icon: <EditIcon />,
              onClick: () => {
                setEditableState(params.row);
              }
            }
          ].map(action => (
            <GridActionsCellItem
              icon={action.icon}
              label={action.label}
              onClick={action.onClick}
              showInMenu
            />
          ))
      }
    ],
    []
  );

  const handlePageChange = newPage => {
    if (newPage === 0 || mapPageToCursor.current[newPage - 1]) {
      setPage(newPage);
    }
  };

  React.useEffect(() => {
    const normalizedDeviceVersion = normalizeDeviceVersion(
      buildModule.deviceVersion
    );

    setShowMaintenanceButton(
      semver.valid(normalizedDeviceVersion) &&
        semver.lt(normalizedDeviceVersion, '1.10.0')
    );
  }, [buildModule.deviceVersion]);

  React.useEffect(() => {
    if (!loading) {
      mapPageToCursor.current[page] = cursor;
    }
  }, [page, loading, cursor]);

  React.useEffect(() => {
    setLoading(true);
    loadStates().then(() => setLoading(false));
  }, [pageSize, page, buildModuleState]);

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end">
        {showMaintenanceButton &&
          (buildModuleState === COMPONENT_STATUSES.MAINTENANCE ? (
            <Button
              variant="contained"
              sx={{ mb: 2 }}
              onClick={async () => {
                await PrinterService.finishMaintenance(buildModule.id);
              }}
            >
              Finish Maintenance
            </Button>
          ) : (
            <Button
              variant="contained"
              sx={{ mb: 2 }}
              onClick={async () => {
                await PrinterService.startMaintenance(buildModule.id);
              }}
            >
              Start Maintenance
            </Button>
          ))}
      </Box>

      <TitanDataGrid
        columns={columns}
        loading={loading}
        rows={rows}
        rowCount={-1}
        getRowId={row => row.timestamp}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        onPageChange={handlePageChange}
        showSearch={false}
        nextPageAvailable={!!cursor}
      />

      {editableState && (
        <StateCommentDialog
          state={editableState}
          onSave={data => {
            PrinterService.updatePrinterState(
              editableState.buildModuleId,
              editableState.timestamp,
              data
            );
            setRows(
              rows.map(state =>
                state.timestamp === editableState.timestamp
                  ? (state = { ...state, ...data })
                  : state
              )
            );
          }}
          onClose={() => setEditableState(null)}
        />
      )}
    </Box>
  );
}
